// Imports
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch } from '../../../../foundation/front-end/redux/hooks';
import {
	GetSuggestionsParameters,
	GetSuggestionsResponse,
} from '../../../../types/api/main/website/address/autocomplete/suggestions/get';
import {
	GetDetailsParameters,
	GetDetailsResponse,
} from '../../../../types/api/main/website/address/autocomplete/details/get';
import debounce from 'lodash/debounce';
import apiActions from '../../../../integrations/api/redux/actions';
import { Address } from '../..';
import { OnPlaceChanged, Regions } from './types';
import useCachedState from '../../../../foundation/front-end/utils/use-cached-state';
import { GetWebsitesResponse } from '../../../../types/api/main/self/websites/get';


// Type guard
const isLobDetails = (
	details: GetSuggestionsResponse[number]['details']
): details is Extract<GetSuggestionsResponse[number]['details'], { streetAddress: string }> =>
	'streetAddress' in details;


// Define options
interface Options {
	disabled?: boolean;
	type?: Address['type'];
	effectiveThreeLetterCountryCode?: string;
	regions: Regions;
	onPlaceChanged: OnPlaceChanged;
	streetAddressSetter: React.MutableRefObject<((value: string) => void) | undefined>;
	streetAddressValue: string;
}


/** React hook that handles working with our API endpoints for autocomplete suggestions and list box */
export default function useApiAutocomplete({
	disabled,
	type,
	effectiveThreeLetterCountryCode,
	regions,
	onPlaceChanged,
	streetAddressSetter,
	streetAddressValue,
}: Options) {
	// Use state
	const [suggestions, setSuggestions] = useState<GetSuggestionsResponse>([]);
	const [isOpen, setIsOpen] = useState(false);
	const [activeIndex, setActiveIndex] = useState(-1);
	const [website] = useCachedState<GetWebsitesResponse[0] | null>(null, 'website');
	
	
	// Use ref
	const containerRef = useRef<HTMLDivElement>(null);
	const listboxRef = useRef<HTMLUListElement>(null);
	const requestIDRef = useRef(0);
	const isProgrammaticChange = useRef(false);
	const hasBeenFocused = useRef(false);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Function that loads suggestions
	const loadSuggestions = useCallback(
		(streetAddressPrefix: string, openListBox = true) => {
			// Initialize and increment
			const requestID = requestIDRef.current + 1;
			requestIDRef.current = requestID;
			
			
			// Call API
			void dispatch(
				apiActions.call<GetSuggestionsParameters, undefined, GetSuggestionsResponse>({
					action: 'GET',
					uri: `/website/${website?.id ?? window.location.hostname}/address/autocomplete/suggestions`,
					parameters: {
						streetAddressPrefix,
						type,
						...(type !== 'INTERNAL' ? { countryCode: effectiveThreeLetterCountryCode } : {}),
					},
					completion: (json) => {
						// Ignore stale responses
						if (requestID !== requestIDRef.current) {
							return;
						}
						
						
						// Update state
						setSuggestions(json.response);
						setActiveIndex(-1);
						
						if (openListBox) {
							setIsOpen(json.response.length > 0);
						}
					},
				})
			);
		},
		[dispatch, type, effectiveThreeLetterCountryCode, website?.id]
	);
	
	
	// Debounced function that loads suggestions
	const debouncedLoadSuggestions = useMemo(() => debounce(loadSuggestions, 200), [loadSuggestions]);
	
	
	// Cancel any pending debounced call on unmount
	useEffect(() => () => debouncedLoadSuggestions.cancel(), [debouncedLoadSuggestions]);
	
	
	// Close dropdown when type or country changes
	useEffect(() => {
		debouncedLoadSuggestions.cancel();
		hasBeenFocused.current = false;
		setIsOpen(false);
	}, [type, effectiveThreeLetterCountryCode, debouncedLoadSuggestions]);
	
	
	// Handle loading suggestions
	useEffect(() => {
		// Return early
		if (disabled || !hasBeenFocused.current) {
			return;
		}
		
		
		// Skip effects caused by selection, not user typing
		if (isProgrammaticChange.current) {
			isProgrammaticChange.current = false;
			return;
		}
		
		
		// Check for minimum required length
		if (streetAddressValue.length < 3) {
			// Cancel any pending calls
			debouncedLoadSuggestions.cancel();
			
			
			// Reset state
			setSuggestions([]);
			setIsOpen(false);
			
			
			// Return
			return;
		}
		
		
		// Load suggestions
		debouncedLoadSuggestions(streetAddressValue);
	}, [disabled, streetAddressValue, debouncedLoadSuggestions]);
	
	
	// Close dropdown when there is a click outside of it
	useEffect(() => {
		if (disabled) {
			return;
		}
		
		const handleClickOutside = (event: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setIsOpen(false);
				setActiveIndex(-1);
			}
		};
		
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [disabled]);
	
	
	// Scroll active item into view
	useEffect(() => {
		if (activeIndex >= 0 && listboxRef.current) {
			const activeItem = listboxRef.current.children[activeIndex] as HTMLElement;
			activeItem?.scrollIntoView({ block: 'nearest' });
		}
	}, [activeIndex]);
	
	
	// A function that handles closing the list box and resetting state
	const closeSuggestions = useCallback(
		(acceptedStreetAddress: string) => {
			debouncedLoadSuggestions.cancel();
			requestIDRef.current += 1;
			isProgrammaticChange.current = true;
			streetAddressSetter.current?.(acceptedStreetAddress);
			setIsOpen(false);
			setActiveIndex(-1);
		},
		[debouncedLoadSuggestions, streetAddressSetter]
	);
	
	
	// Handle a Lob suggestion being selected
	const handleLobSuggestionSelect = useCallback(
		(selectedSuggestion: GetSuggestionsResponse[number]) => {
			const details = selectedSuggestion.details;
			
			if (!isLobDetails(details)) {
				return;
			}
			
			closeSuggestions(details.streetAddress);
			
			onPlaceChanged(
				{
					streetAddress: details.streetAddress,
					countryCode: details.countryCode,
					regionID: details.regionID,
					postalCode: details.postalCode,
					locality: details.division1 ?? details.division2 ?? undefined,
				},
				{
					type: 'UPDATE',
					locationID: selectedSuggestion.alternateLocationID ?? null,
				}
			);
		},
		[closeSuggestions, onPlaceChanged]
	);
	
	
	// Handle a Google suggestion being selected
	const handleGoogleSuggestionSelect = useCallback(
		(placeID: string, formattedStreetAddress: string) => {
			// Set the street address optimistically
			closeSuggestions(formattedStreetAddress);
			
			
			// Call API
			void dispatch(
				apiActions.call<GetDetailsParameters, undefined, GetDetailsResponse>({
					action: 'GET',
					uri: `/website/${website?.id ?? window.location.hostname}/address/autocomplete/details`,
					parameters: { placeID },
					completion: (json) => {
						// Initialize
						const details = json.response;
						
						
						// Handle potentially misaligned state
						const streetAddress =
							details.streetAddress && !details.streetAddress.includes('undefined')
								? details.streetAddress
								: formattedStreetAddress;
						
						if (streetAddress !== formattedStreetAddress) {
							closeSuggestions(streetAddress);
						}
						
						
						// Determine region ID from name
						const regionID = details.region
							? Object.values(regions?.find((region) => region.code === details.country.code)?.regions ?? {})
									.flat()
									.find((region) => region.name === details.region?.name)?.id
							: undefined;
						
						
						// Call prop
						onPlaceChanged(
							{
								streetAddress: streetAddress,
								countryCode: details.country.code,
								regionID: regionID,
								postalCode: details.postalCode ?? undefined,
								locality: details.division1 ?? details.division2 ?? undefined,
							},
							{ type: 'PRESERVE' }
						);
					},
				})
			);
		},
		[closeSuggestions, dispatch, onPlaceChanged, regions, website?.id]
	);
	
	
	// Determine which suggestion detail handler to call
	const handleSuggestionSelect = useCallback(
		(suggestion: GetSuggestionsResponse[number]) => {
			if (isLobDetails(suggestion.details)) {
				handleLobSuggestionSelect(suggestion);
			} else {
				const [formattedStreetAddress] = suggestion.formatted.split('\n');
				handleGoogleSuggestionSelect(suggestion.details.id, formattedStreetAddress);
			}
		},
		[handleLobSuggestionSelect, handleGoogleSuggestionSelect]
	);
	
	
	// Handle keyboard navigation inside the list box
	const handleKeyDown = (event: React.KeyboardEvent) => {
		// Prevent form submission
		if (event.key === 'Enter') {
			event.preventDefault();
		}
		
		
		// Return early
		if (!isOpen) {
			return;
		}
		
		
		// Handle other relevant key presses
		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault();
				setActiveIndex((prev) => (prev + 1 >= suggestions.length ? 0 : prev + 1));
				
				break;
			case 'ArrowUp':
				event.preventDefault();
				setActiveIndex((prev) => (prev - 1 < 0 ? suggestions.length - 1 : prev - 1));
				
				break;
			case 'Enter':
				if (activeIndex >= 0 && suggestions[activeIndex]) {
					handleSuggestionSelect(suggestions[activeIndex]);
				}
				
				break;
			case 'Escape':
			case 'Tab':
				setIsOpen(false);
				setActiveIndex(-1);
				
				break;
		}
	};
	
	
	// Handle focus
	const handleFocus = useCallback(() => {
		hasBeenFocused.current = true;
		
		if (!disabled && streetAddressValue.length >= 3) {
			loadSuggestions(streetAddressValue);
		}
	}, [disabled, streetAddressValue, loadSuggestions]);
	
	
	// Return
	return {
		suggestions,
		isOpen,
		activeIndex,
		containerRef,
		listboxRef,
		setIsOpen,
		setActiveIndex,
		handleSuggestionSelect,
		handleKeyDown,
		handleFocus,
	};
}
